"use client";

import React, { useState, useEffect, useMemo } from "react";
import {
  ShieldCheck,
  Plus,
  Search,
  Edit2,
  Trash2,
  Users,
  Briefcase,
  AlertCircle,
  RefreshCw,
  Lock,
  CheckCircle2,
  X,
  Check
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "react-hot-toast";
import { roleManagementService } from "../apis/roleManagementService";
import { isCoreSystemRole, getCoreRoleIndex } from "../constants/roles";
import PermissionManagementModal from "./permission-management-modal";

export function RoleManagementModal({ isOpen, onClose, onRolesUpdated }) {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // Add Role state (inline)
  const [newRoleName, setNewRoleName] = useState("");
  const [isAdding, setIsAdding] = useState(false);

  // Inline edit state
  const [editingRoleId, setEditingRoleId] = useState(null);
  const [editingRoleName, setEditingRoleName] = useState("");
  const [isSavingEdit, setIsSavingEdit] = useState(false);

  // Delete state
  const [deletingRole, setDeletingRole] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Tab permissions modal state
  const [permissionRole, setPermissionRole] = useState(null);

  const fetchRoles = async () => {
    try {
      setLoading(true);
      const res = await roleManagementService.getAllRoles();
      if (res.success && Array.isArray(res.data)) {
        setRoles(res.data);
      } else {
        toast.error(res.error || "Failed to load roles");
      }
    } catch (err) {
      console.error("Error fetching roles in modal:", err);
      toast.error("Failed to load roles");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchRoles();
      setNewRoleName("");
      setSearchTerm("");
      setEditingRoleId(null);
      setDeletingRole(null);
    }
  }, [isOpen]);

  // Handle Add Role
  const handleAddRole = async (e) => {
    if (e) e.preventDefault();
    const trimmed = newRoleName.trim();
    if (!trimmed) {
      toast.error("Please enter a role name");
      return;
    }

    try {
      setIsAdding(true);
      const res = await roleManagementService.createRole(trimmed);
      if (res.success) {
        toast.success(`Role "${trimmed}" created successfully!`);
        setNewRoleName("");
        await fetchRoles();
        onRolesUpdated?.();
      } else {
        toast.error(res.error || "Failed to create role");
      }
    } catch (err) {
      toast.error("An error occurred while creating role");
    } finally {
      setIsAdding(false);
    }
  };

  // Handle Inline Edit Save
  const handleSaveEdit = async (id) => {
    const trimmed = editingRoleName.trim();
    if (!trimmed) {
      toast.error("Role name cannot be empty");
      return;
    }

    try {
      setIsSavingEdit(true);
      const res = await roleManagementService.updateRole(id, trimmed);
      if (res.success) {
        toast.success(res.message || "Role updated successfully");
        setEditingRoleId(null);
        await fetchRoles();
        onRolesUpdated?.();
      } else {
        toast.error(res.error || "Failed to update role");
      }
    } catch (err) {
      toast.error("An error occurred while updating role");
    } finally {
      setIsSavingEdit(false);
    }
  };

  // Handle Delete Role Confirm
  const handleDeleteConfirm = async () => {
    if (!deletingRole) return;
    try {
      setIsDeleting(true);
      const res = await roleManagementService.deleteRole(deletingRole.id);
      if (res.success) {
        toast.success(`Role "${deletingRole.role}" deleted successfully`);
        setDeletingRole(null);
        await fetchRoles();
        onRolesUpdated?.();
      } else {
        toast.error(res.error || "Failed to delete role");
      }
    } catch (err) {
      toast.error("An error occurred while deleting role");
    } finally {
      setIsDeleting(false);
    }
  };

  // Filtered Roles sorted with core system roles first
  const filteredRoles = useMemo(() => {
    const list = !searchTerm
      ? roles
      : roles.filter((r) => r.role.toLowerCase().includes(searchTerm.toLowerCase()));

    return [...list].sort((a, b) => {
      const indexA = getCoreRoleIndex(a.role);
      const indexB = getCoreRoleIndex(b.role);
      if (indexA !== indexB) {
        return indexA - indexB;
      }
      return (a.id || 0) - (b.id || 0);
    });
  }, [roles, searchTerm]);

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl p-0 overflow-hidden bg-white border border-gray-200 shadow-2xl rounded-2xl">
        {/* Modal Header */}
        <DialogHeader className="px-6 py-5 bg-gradient-to-r from-gray-900 to-gray-800 text-white">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-yellow-500 rounded-xl text-black shadow-md">
              <ShieldCheck className="h-6 w-6" />
            </div>
            <div>
              <DialogTitle className="text-xl font-bold text-white flex items-center gap-2">
                Dynamic Role Management
                <span className="text-xs font-normal text-yellow-400 bg-yellow-500/10 border border-yellow-500/30 px-2 py-0.5 rounded-full">
                  Admin Control
                </span>
              </DialogTitle>
              <p className="text-xs text-gray-300 mt-0.5">
                Roles configured here automatically populate into Employee & User Management dropdowns.
              </p>
            </div>
          </div>
        </DialogHeader>

        <div className="p-6 space-y-5">
          {/* Add New Role Bar */}
          <form onSubmit={handleAddRole} className="p-3.5 bg-amber-50/70 border border-yellow-200 rounded-xl space-y-2">
            <div className="flex items-center justify-between text-xs font-semibold text-amber-900">
              <span className="flex items-center gap-1.5">
                <Plus className="w-3.5 h-3.5 text-yellow-600 stroke-[3]" />
                Add New Operational Role
              </span>
              <span className="text-[11px] text-gray-500 font-normal">
                Immediately available across forms
              </span>
            </div>

            <div className="flex items-center gap-2">
              <Input
                type="text"
                placeholder="e.g. Pit Boss, Valet, Security, VIP Host"
                value={newRoleName}
                onChange={(e) => setNewRoleName(e.target.value)}
                className="text-xs h-9 bg-white"
                disabled={isAdding}
              />
              <Button
                type="submit"
                disabled={isAdding || !newRoleName.trim()}
                className="bg-yellow-500 hover:bg-yellow-600 text-black font-bold text-xs h-9 px-4 flex-shrink-0"
              >
                {isAdding ? "Adding..." : "Add Role"}
              </Button>
            </div>
          </form>

          {/* Search Filter */}
          <div className="flex items-center justify-between gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
              <Input
                type="text"
                placeholder="Filter roles..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 h-8 text-xs bg-white"
              />
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={fetchRoles}
              disabled={loading}
              className="h-8 text-xs flex items-center gap-1.5 bg-white"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          </div>

          {/* Roles List */}
          <div className="border border-gray-200 rounded-xl overflow-hidden bg-white shadow-xs">
            {loading ? (
              <div className="py-12 text-center text-gray-500">
                <RefreshCw className="h-6 w-6 animate-spin mx-auto text-yellow-500 mb-2" />
                <p className="text-xs">Loading roles...</p>
              </div>
            ) : filteredRoles.length === 0 ? (
              <div className="py-12 text-center text-gray-400 text-xs">
                No roles found matching "{searchTerm}"
              </div>
            ) : (
              <div className="max-h-72 overflow-y-auto divide-y divide-gray-100">
                {filteredRoles.map((item) => {
                  const isProtected = item.is_protected || isCoreSystemRole(item.role);
                  const isEditingThis = editingRoleId === item.id;
                  const hasAssigned = item.user_count > 0 || item.employee_count > 0;

                  return (
                    <div
                      key={item.id}
                      className="p-3 px-4 hover:bg-gray-50/70 transition flex items-center justify-between gap-3 text-xs"
                    >
                      {/* Left: Role Info or Edit Input */}
                      <div className="flex items-center gap-2.5 flex-1 min-w-0">
                        <div
                          className={`w-7 h-7 rounded-lg flex items-center justify-center font-bold text-[11px] flex-shrink-0 ${
                            isProtected
                              ? "bg-amber-100 text-amber-800"
                              : "bg-blue-100 text-blue-800"
                          }`}
                        >
                          {item.role.substring(0, 2).toUpperCase()}
                        </div>

                        {isEditingThis ? (
                          <div className="flex items-center gap-2 flex-1 mr-2">
                            <Input
                              type="text"
                              value={editingRoleName}
                              onChange={(e) => setEditingRoleName(e.target.value)}
                              className="h-7 text-xs bg-white"
                              autoFocus
                            />
                            <Button
                              size="sm"
                              onClick={() => handleSaveEdit(item.id)}
                              disabled={isSavingEdit || !editingRoleName.trim()}
                              className="h-7 px-2 bg-blue-600 hover:bg-blue-700 text-white text-[11px]"
                            >
                              <Check className="w-3.5 h-3.5" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setEditingRoleId(null)}
                              disabled={isSavingEdit}
                              className="h-7 px-2 text-gray-500 text-[11px]"
                            >
                              <X className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        ) : (
                          <div className="truncate">
                            <div className="font-bold text-gray-900 text-sm truncate">{item.role}</div>
                            <div className="text-[10px] text-gray-400">ID #{item.id}</div>
                          </div>
                        )}
                      </div>

                      {/* Middle: Badges */}
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {isProtected ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-200">
                            <Lock className="w-2.5 h-2.5" />
                            Core
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                            <CheckCircle2 className="w-2.5 h-2.5" />
                            Custom
                          </span>
                        )}

                        {item.employee_count > 0 && (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-50 text-blue-700">
                            <Briefcase className="w-2.5 h-2.5" />
                            {item.employee_count} emp
                          </span>
                        )}

                        {item.user_count > 0 && (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-purple-50 text-purple-700">
                            <Users className="w-2.5 h-2.5" />
                            {item.user_count} usr
                          </span>
                        )}
                      </div>

                      {/* Right: Actions */}
                      <div className="flex items-center justify-end gap-1 flex-shrink-0 w-24">
                        {isProtected ? (
                          <>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setPermissionRole(item)}
                              className="h-7 w-7 p-0 text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50"
                              title={`Configure tab access permissions for ${item.role}`}
                            >
                              <ShieldCheck className="w-3.5 h-3.5" />
                            </Button>
                            <span className="text-[10px] text-gray-400 italic px-1">Core</span>
                          </>
                        ) : !isEditingThis ? (
                          <>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                setEditingRoleId(item.id);
                                setEditingRoleName(item.role);
                              }}
                              className="h-7 w-7 p-0 text-gray-500 hover:text-blue-600 hover:bg-blue-50"
                              title="Edit role name"
                            >
                              <Edit2 className="w-3.5 h-3.5" />
                            </Button>

                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setDeletingRole(item)}
                              className={`h-7 w-7 p-0 ${
                                hasAssigned
                                  ? "text-gray-300 hover:text-gray-400"
                                  : "text-gray-500 hover:text-red-600 hover:bg-red-50"
                              }`}
                              title={
                                hasAssigned
                                  ? `Assigned to ${item.employee_count} emp, ${item.user_count} usr`
                                  : "Delete role"
                              }
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Delete Confirmation Box (if a role is selected for deletion) */}
          {deletingRole && (
            <div className="p-3.5 bg-red-50 border border-red-200 rounded-xl space-y-2 animate-in fade-in-50">
              <div className="flex items-center gap-2 text-red-700 font-bold text-xs">
                <AlertCircle className="w-4 h-4" />
                <span>Delete Role: "{deletingRole.role}"?</span>
              </div>

              {deletingRole.user_count > 0 || deletingRole.employee_count > 0 ? (
                <div className="text-xs text-red-700 space-y-1">
                  <p>
                    Cannot delete because this role is currently assigned to{" "}
                    <strong>{deletingRole.employee_count} employee(s)</strong> and{" "}
                    <strong>{deletingRole.user_count} user(s)</strong>.
                  </p>
                  <p className="text-[11px] text-gray-600">
                    Please reassign these accounts before deleting this role.
                  </p>
                  <div className="flex justify-end pt-1">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setDeletingRole(null)}
                      className="text-xs h-7"
                    >
                      Dismiss
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between text-xs pt-1">
                  <span className="text-gray-600">
                    This will permanently remove the role from the catalog.
                  </span>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setDeletingRole(null)}
                      disabled={isDeleting}
                      className="text-xs h-7"
                    >
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      onClick={handleDeleteConfirm}
                      disabled={isDeleting}
                      className="bg-red-600 hover:bg-red-700 text-white font-bold text-xs h-7"
                    >
                      {isDeleting ? "Deleting..." : "Confirm Delete"}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Footer Action */}
          <div className="flex justify-end pt-2 border-t border-gray-100">
            <Button
              variant="outline"
              onClick={onClose}
              className="text-xs"
            >
              Close
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>

    {permissionRole && (
      <PermissionManagementModal
        isOpen={Boolean(permissionRole)}
        onClose={() => setPermissionRole(null)}
        targetType="role"
        targetId={permissionRole.id}
        targetName={permissionRole.role}
        role={permissionRole}
      />
    )}
  </>
  );
}

export default RoleManagementModal;
